// SAFE-WRITE-BEGIN: _SignOffActionBar.tsx
/**
 * VAL-FINALIZE-LAYOUT-1 L1 — the sticky "Next step" bar on the Finalize screen.
 *
 * It decides nothing and runs nothing of its own: every button here calls the
 * handler the page already passes to the card it mirrors (`onFinalize`,
 * `onProduce`, the shared document download), under the card's own label.
 * The single action shown is the first that applies:
 *   0. not signed yet                                    -> Finalize and sign
 *   B. signed, inputs changed since vN                   -> Re-finalize as v{N+1} (opens the card)
 *   C. signed, no document yet                           -> Produce document
 *   A. a document is on file                             -> Download PDF
 */
import { Button } from '@/components/ui/button'
import type { ValuationDocumentRow } from '@/api/valuation'
import { DELIVERED_BAR_TEXT, changedBarText, finalizeLabel, producedPendingBarText } from './_SignOffGates'
import { EmailDocumentButton } from './_EmailDocument'
import { isPdf, useDocumentDownload } from './_OverviewDocuments'

type Tick = 'ok' | 'blocked' | 'unknown'

function Check({ state, label }: { state: Tick; label: string }) {
  const mark = state === 'ok' ? '✓' : state === 'blocked' ? '✗' : '…'
  const color = state === 'ok' ? 'text-green-700' : state === 'blocked' ? 'text-red-700' : 'text-gray-500'
  return (
    <li className="flex items-center gap-1.5">
      <span aria-hidden="true" className={color}>{mark}</span>
      <span>{label}</span>
      <span className="sr-only">{state === 'ok' ? 'Met' : state === 'unknown' ? 'Pending' : 'Not met'}</span>
    </li>
  )
}

export function SignOffActionBar({
  engagementId,
  hasComputedValue,
  concluded,
  canConclude,
  engineOk,
  liveVersion,
  willSign,
  reissueChanged,
  awaitingSignature = false,
  finalizing,
  canFinalize,
  gateOk,
  blockedReason,
  onFinalize,
  documentsOnFile,
  canProduce,
  producing,
  onProduce,
  documents,
  changedFieldLabels,
  onOpenNewVersion,
}: {
  engagementId: string
  hasComputedValue: boolean
  concluded: boolean
  canConclude: boolean
  /** Engine check: true clean, false blocked, null not back yet. */
  engineOk: boolean | null
  liveVersion: number | null
  willSign: boolean
  /** Same value the Finalize card gets: null on a first finalize or while checking. */
  reissueChanged: boolean | null
  /** Latest version recorded, not signed by a signer; a signer can sign it unchanged. */
  awaitingSignature?: boolean
  finalizing: boolean
  canFinalize: boolean
  /** Every finalize condition except the sign-off reason terms. */
  gateOk: boolean
  /** The Finalize card's own blocked-reason text, passed through unchanged. */
  blockedReason: string | null
  onFinalize: () => void
  documentsOnFile: boolean
  canProduce: boolean
  producing: boolean
  onProduce: () => void
  documents: ValuationDocumentRow[]
  /** Labels of what changed since the signed version (state B). */
  changedFieldLabels: string[]
  /** State B: open the new-version card, scroll to it, focus the sign-off reason. */
  onOpenNewVersion: () => void
}) {
  const { downloadingId, handleDownload } = useDocumentDownload(engagementId)
  const livePdf = [...documents]
    .filter(d => d.snapshot_version === liveVersion && isPdf(d))
    .sort((a, b) => new Date(b.produced_at).getTime() - new Date(a.produced_at).getTime())[0]

  function focusReason() {
    const box = document.getElementById('signoff-rationale')
    box?.scrollIntoView?.({ block: 'center' })
    box?.focus()
  }

  // VAL-FINALIZE-STATE-2: the bar names one state and shows one main action.
  //   first finalize  -> Finalize and sign / Record calculation
  //   B  signed, inputs changed since vN   -> Re-finalize as v{N+1} (opens the new-version card)
  //   A  signed, documents on file         -> Download PDF
  //   C  signed, no documents yet          -> Produce document (never Re-finalize)
  const changed = liveVersion !== null && reissueChanged === true
  const pdfButtons = livePdf && (
    <>
      <EmailDocumentButton
        engagementId={engagementId}
        liveVersion={liveVersion}
        documentsOnFile={documentsOnFile}
        showStatus={false}
      />
      <Button disabled={downloadingId === livePdf.document_id} onClick={() => handleDownload(livePdf)}>
        Download PDF
      </Button>
    </>
  )
  // FIX_VAL-SIGNOFF-MESSAGES-1 M2: files on file are delivered only once the server allows producing.
  const delivered = documentsOnFile && canProduce
  let action: React.ReactNode = null
  let why: string | null = null
  let list: string | null = null
  if (liveVersion === null) {
    why = blockedReason
    action = (
      <Button disabled={!gateOk || finalizing} onClick={() => (canFinalize ? onFinalize() : focusReason())}>
        {finalizeLabel(finalizing, liveVersion, willSign)}
      </Button>
    )
  } else if (changed) {
    why = changedBarText(liveVersion)
    if (changedFieldLabels.length > 0) list = `Changed since v${liveVersion}: ${changedFieldLabels.join(', ')}.`
    action = (
      <Button disabled={finalizing} onClick={onOpenNewVersion}>
        {finalizeLabel(finalizing, liveVersion, willSign)}
      </Button>
    )
  } else if (awaitingSignature && !delivered) {
    action = (
      <Button disabled={finalizing} onClick={onOpenNewVersion}>
        {finalizeLabel(finalizing, liveVersion, willSign)}
      </Button>
    )
  } else if (delivered) {
    why = DELIVERED_BAR_TEXT
    action = pdfButtons
  } else {
    if (canProduce) why = producedPendingBarText(liveVersion)
    action = (
      <Button disabled={producing || !canProduce} onClick={onProduce}>
        {producing ? 'Producing…' : 'Produce document'}
      </Button>
    )
  }

  return (
    <section
      aria-label="Next step"
      className="sticky bottom-0 z-10 -mx-6 flex flex-wrap items-center justify-between gap-3 border-t bg-white px-6 py-3 shadow-[0_-2px_6px_rgba(0,0,0,0.06)]"
    >
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
        <Check state={hasComputedValue ? 'ok' : 'blocked'} label="Value calculated" />
        <Check
          state={concluded ? (canConclude ? 'ok' : 'blocked') : 'ok'}
          label="Role can finalize"
        />
        <Check state={engineOk === null ? 'unknown' : engineOk ? 'ok' : 'blocked'} label="Engine check" />
      </ul>
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
        {list && <p className="min-w-0 max-w-full text-xs text-gray-600">{list}</p>}
        {why && (
          <p className={liveVersion !== null ? 'min-w-0 max-w-full text-sm font-medium text-gray-900' : 'min-w-0 max-w-full text-xs text-amber-800'}>
            {why}
          </p>
        )}
        {action}
      </div>
    </section>
  )
}
// SAFE-WRITE-EOF: _SignOffActionBar.tsx
